import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import useStore from '../../store/useStore';
import {
  deleteVideo,
  formatDate,
  formatDuration,
  formatSize,
  listStudentVideos,
  listVideos,
} from './videoApi';

// Saved clips — of one profile, or with `studentId` of the whole student
// (every assessment, labelled). Thumbnail / inline player, duration, size,
// date, delete (confirmed in a dialog; shown only where the server allows it).
// `refreshKey` reloads the list.
export default function AssessmentVideoList({
  profileId,
  studentId,
  canEdit = false,
  refreshKey = 0,
  onCountChange,
}) {
  const { language } = useStore();
  const en = language === 'en';
  const [videos, setVideos] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(() => {
    if (!profileId && !studentId) return undefined;
    let cancelled = false;
    setError('');
    (studentId ? listStudentVideos(studentId) : listVideos(profileId))
      .then((list) => {
        if (cancelled) return;
        setVideos(list);
        if (onCountChange) onCountChange(list.length);
      })
      .catch((err) => {
        if (cancelled) return;
        setVideos((prev) => prev || []);
        setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId, studentId, onCountChange]);

  useEffect(() => {
    setVideos(null);
    setPlayingId(null);
    return load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, studentId, refreshKey]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteVideo(deleteTarget.profileId || profileId, deleteTarget._id);
      setVideos((prev) => {
        const next = (prev || []).filter((v) => v._id !== deleteTarget._id);
        if (onCountChange) onCountChange(next.length);
        return next;
      });
      if (playingId === deleteTarget._id) setPlayingId(null);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (videos === null) {
    return (
      <Box sx={{ py: 1.5, textAlign: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              {en ? 'Retry' : 'Повторить'}
            </Button>
          }
        >
          {en ? 'Could not load videos' : 'Не удалось загрузить видео'}: {error}
        </Alert>
      )}
      {!error && videos.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {studentId
            ? en
              ? 'No videos of this student yet.'
              : 'Видео этого ученика пока нет.'
            : en
              ? 'No videos for this assessment yet.'
              : 'Для этой оценки пока нет видео.'}
        </Typography>
      )}
      <Stack spacing={1}>
        {videos.map((v) => {
          const playing = playingId === v._id;
          return (
            <Box
              key={v._id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {!playing && (
                  <Box
                    onClick={() => setPlayingId(v._id)}
                    role="button"
                    tabIndex={0}
                    aria-label={en ? 'Play' : 'Смотреть'}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPlayingId(v._id);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      width: 96,
                      height: 64,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      bgcolor: '#000',
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      component="video"
                      src={`${v.url}#t=0.1`}
                      preload="metadata"
                      muted
                      playsInline
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        pointerEvents: 'none',
                      }}
                    />
                    <PlayCircleOutlineIcon
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        m: 'auto',
                        color: '#fff',
                        fontSize: 32,
                        filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {v.title || (en ? 'Video' : 'Видео')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {v.durationSec ? `${formatDuration(v.durationSec)} · ` : ''}
                    {formatSize(v.size)} · {formatDate(v.uploadedAt, language)}
                  </Typography>
                  {studentId && (v.templateName || v.profileName) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: 'block' }}
                    >
                      {[v.templateName, v.profileName]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
                {(v.canDelete !== undefined ? v.canDelete : canEdit) && (
                  <Tooltip title={en ? 'Delete video' : 'Удалить видео'}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleteError('');
                        setDeleteTarget(v);
                      }}
                      aria-label={en ? 'Delete video' : 'Удалить видео'}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {playing && (
                <Box
                  component="video"
                  src={v.url}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  sx={{
                    width: '100%',
                    maxHeight: '60vh',
                    bgcolor: '#000',
                    borderRadius: 1.5,
                    display: 'block',
                    mt: 1,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {en ? 'Delete this video?' : 'Удалить видео?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {en
              ? 'The recording will be removed permanently.'
              : 'Запись будет удалена без возможности восстановления.'}
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {en ? 'Cancel' : 'Отмена'}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? '...' : en ? 'Delete' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
